import { useModalStore } from '@/shared/lib';
import { NUMERIC_CONFIG } from '@/shared/config';
import { useTranslation } from 'react-i18next';
// import { WithdrawHistoryModal } from '@/shared/components';
// import type { OperationHistoryParams } from '@/shared/types';
import {
  ResultModal,
  WithdrawBankTransferModal,
  type WithdrawBankTransferState,
  WithdrawConfirmationModalContainer,
} from '@/features/withdraw-bank-transfer';
import { getCalculatedCommission } from '@/entities/operation';
import { useEditBankAddressModal } from '@/features/edit-bank-address';
import type {
  BankAddressStepResponse,
  SavedBankAddress,
} from '@/entities/bank-address';
import { useCreateSavedAddressModal } from '@/widgets/create-saved-address-modal';
import { normalizeBankAddressParams } from '@/features/withdraw-bank-transfer/lib/normalizeBankAddressParams.ts';

export function useWithdrawBankTransferModals() {
  const {
    showDialogComponent,
    closeDialog,
    getState,
    cleanup,
    getAllState,
    setState,
  } = useModalStore();
  const { t } = useTranslation();

  const openOperationMethodsModal = getState(
    'onWithdrawFormBack'
  ) as () => void;
  // TODO think about following FSD pattern
  const { openCreateSavedAddressModal } = useCreateSavedAddressModal();
  const { openEditBankAddressModal } = useEditBankAddressModal();

  const onCloseDialog = () => {
    cleanup();
    closeDialog();
  };

  const cleanupStateWithoutBack = () => {
    const onWithdrawFormBack = getState('onWithdrawFormBack');
    cleanup();
    setState('onWithdrawFormBack', onWithdrawFormBack);
  };

  const onResultModalBack = () => {
    cleanupStateWithoutBack();
    openWithdrawBankTransferModal();
  };

  const closeConfirmation = {
    title: t('withdrawMethods.crypto.exitConfirmTitle'),
    description: t('withdrawMethods.crypto.exitConfirmDescription'),
    confirmText: t('withdrawMethods.crypto.exitConfirmButton'),
    showCancelButton: false,
  };

  function getStoreData() {
    const storeData = getAllState<WithdrawBankTransferState>();
    const lastStep =
      storeData?.formStepsData &&
      storeData.formStepsData[storeData.formStepsData.length - 1];

    let currencyCode = '';

    if (lastStep) {
      currencyCode = lastStep.amountCurrency || '';
    }

    const commissionData = getCalculatedCommission({
      amount: storeData?.totalAmount,
      assetCode: currencyCode,
      includeFee: !!lastStep?.values?.includeFee,
      precision: NUMERIC_CONFIG.precision.default,
      fee: storeData.feeAmount,
    });

    return {
      commissionData,
      storeData,
    };
  }

  const openResultModal = ({ success }: { success: boolean }) => {
    const { commissionData, storeData } = getStoreData();

    const initialBankFormFields = (storeData.formStepsData.at(-1) ??
      {}) as BankAddressStepResponse;

    const normalizedFields = {
      ...initialBankFormFields,
      params: normalizeBankAddressParams(initialBankFormFields.params ?? []),
    };

    showDialogComponent({
      component: (
        <ResultModal
          success={success}
          data={{
            commissionData,
            storeData,
          }}
          onClose={() => {
            if (success) {
              onCloseDialog();
              return;
            }

            onResultModalBack();
          }}
          isNeedToSaveTemplate={storeData.isNeedToSaveTemplate}
          onSaveAddress={() => {
            openCreateSavedAddressModal({
              onClose: cleanupStateWithoutBack,
              onBack: onResultModalBack,
              onCreateSuccess: () => {
                cleanupStateWithoutBack();
                openOperationMethodsModal();
              },
              showOnlyTab: 'bank',
              initialBankFormFields:
                normalizedFields.params?.length > 0
                  ? normalizedFields
                  : undefined,
            });
          }}
        />
      ),
      onClose: onCloseDialog,
    });
  };

  // const openWithdrawHistory = (historyParams?: OperationHistoryParams) => {
  //   showDialogComponent({
  //     component: (
  //       <WithdrawHistoryModal
  //         onBack={openWithdrawBankTransferModal}
  //         title={t('withdrawMethods.crypto.history.dialogTitle')}
  //         historyParams={historyParams}
  //       />
  //     ),
  //     onClose: onCloseDialog,
  //     closeConfirmation,
  //     skipBackButtonConfirmation: true,
  //     closeConfirmationRequired: true,
  //   });
  // };

  const handleEditSavedBankAddress = (savedBankAddress: SavedBankAddress) => {
    openEditBankAddressModal({
      savedBankAddress,
      onBack: openWithdrawBankTransferModal,
    });
  };

  const openWithdrawBankTransferModal = (assetCode?: string) => {
    const openOperationMethodsModal = getState(
      'onWithdrawFormBack'
    ) as () => void;

    showDialogComponent({
      component: (
        <WithdrawBankTransferModal
          // Withdraw history is temporarily disabled
          // onHistoryClick={openWithdrawHistory}
          assetCode={assetCode}
          title={t('withdrawMethods.bankTransfer.title')}
          onBack={() => {
            openOperationMethodsModal();
            cleanup();
          }}
          onEditSavedBankAddress={handleEditSavedBankAddress}
        />
      ),
      className: 'overflow-hidden',
      onClose: onCloseDialog,
      preventOverlayClose: true,
      closeConfirmation,
      skipBackButtonConfirmation: false,
    });
  };

  const openConfirmationModal = (params?: { continueMFA?: boolean }) => {
    const { commissionData, storeData } = getStoreData();

    showDialogComponent({
      component: (
        <WithdrawConfirmationModalContainer
          continueMFA={params?.continueMFA}
          onBack={openWithdrawBankTransferModal}
          storeData={storeData}
          commissionData={commissionData}
        />
      ),
      onClose: onCloseDialog,
      preventOverlayClose: true,
      closeConfirmation,
      skipBackButtonConfirmation: true,
      closeConfirmationRequired: true,
    });
  };

  return {
    openResultModal,
    openWithdrawBankTransferModal,
    openConfirmationModal,
  };
}
